import { useRef } from 'react';
import api from 'api';
import { actions } from 'store';
import { useDispatch, useReduxState } from 'hooks';
import { ConfirmButton, FlexContainer, MoreOptions } from 'components/common';
import {
  Checkbox,
  ColorPicker,
  Form,
  FormGroup,
  FormSection,
  Text
} from 'components/common/form';
import { SiteSettingsThemesExport, SiteSettingsThemesImport } from '.';

import type { FormApi } from 'informed';
import type { ReaderTheme } from 'shared-types';

interface Props {
  onSave: () => void;
  readerTheme: ReaderTheme;
  readerThemeId: number;
  title: string;
}

const SiteSettingsThemesEditorReader = ({
  onSave,
  readerTheme,
  readerThemeId,
  title
}: Props) => {
  const formApi = useRef<FormApi>(undefined);

  const { dispatch } = useDispatch();

  const { messages } = useReduxState();

  const handleChange = async (values) => {
    try {
      setTimeout(() => {
        dispatch(
          actions.updateReaderTheme(readerThemeId, {
            ...readerTheme,
            ...values.data
          })
        );
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const handleSubmit = async () => {
    const { values } = formApi.current.getState();
    await api.put(`/admin/server-themes/${readerThemeId}`, values);
    await onSave();
    dispatch(
      actions.addNotice({
        text: messages.siteThemesUpdateSuccess,
        type: 'success'
      })
    );
    return true;
  };

  return (
    <Form
      getApi={(api) => (formApi.current = api)}
      initialValues={{
        title,
        data: {
          sidebarDarkMode: true,
          tocChapterBorderColor: 'rgba(0, 0, 0, 0.1)',
          tocSubheadingsBorderColor: 'rgba(0, 0, 0, 0.1)',
          ...readerTheme
        }
      }}
      onValueChange={handleChange}
      style={{ height: '100%' }}
    >
      {({ formState }) => (
        <FlexContainer style={{ height: '100%', flexDirection: 'column' }}>
          <div style={{ flexGrow: 1, overflowY: 'auto', width: '100%' }}>
            <FormSection>
              <FormGroup label={messages.commonTitle}>
                <Text field="title" style={{ maxWidth: '100%', width: 300 }} />
              </FormGroup>

              <FormGroup label="backgroundColor">
                <ColorPicker field="data.backgroundColor" />
              </FormGroup>
            </FormSection>

            <FormSection maxWidth="100%" title="Header">
              <FormGroup label="headerBackgroundColor">
                <ColorPicker field="data.headerBackgroundColor" />
              </FormGroup>

              <FormGroup label="headerColor">
                <ColorPicker field="data.headerColor" />
              </FormGroup>

              <FormGroup label="headerColorActive">
                <ColorPicker field="data.headerColorActive" />
              </FormGroup>

              <FormGroup label="headerFocusColor">
                <ColorPicker field="data.headerFocusColor" />
              </FormGroup>
            </FormSection>

            <FormSection maxWidth="100%" title="Sidebar">
              <FormGroup label="sidebarBackgroundColor">
                <ColorPicker field="data.sidebarBackgroundColor" />
              </FormGroup>

              <FormGroup label="sidebarHeaderBackgroundColor">
                <ColorPicker field="data.sidebarHeaderBackgroundColor" />
              </FormGroup>

              <FormGroup label="sidebarHeaderColor">
                <ColorPicker field="data.sidebarHeaderColor" />
              </FormGroup>

              <FormGroup label="sidebarHeaderColorActive">
                <ColorPicker field="data.sidebarHeaderColorActive" />
              </FormGroup>

              <FormGroup label="sidebarColor">
                <ColorPicker field="data.sidebarColor" />
              </FormGroup>

              <FormGroup label="sidebarColorActive">
                <ColorPicker field="data.sidebarColorActive" />
              </FormGroup>

              <FormGroup label="sidebarInnerBackgroundColor">
                <ColorPicker field="data.sidebarInnerBackgroundColor" />
              </FormGroup>

              <FormGroup label="sidebarInnerColor">
                <ColorPicker field="data.sidebarInnerColor" />
              </FormGroup>

              <FormGroup label="sidebarInnerColorActive">
                <ColorPicker field="data.sidebarInnerColorActive" />
              </FormGroup>

              <FormGroup>
                <Checkbox
                  field="data.sidebarDarkMode"
                  id="sidebar-dark-mode"
                  label="sidebarDarkMode"
                />
              </FormGroup>
            </FormSection>

            <FormSection maxWidth="100%" title="Table of contents">
              <FormGroup label="tocNumberBackgroundColor">
                <ColorPicker field="data.tocNumberBackgroundColor" />
              </FormGroup>

              <FormGroup label="tocNumberColor">
                <ColorPicker field="data.tocNumberColor" />
              </FormGroup>

              <FormGroup label="tocNumberBackgroundColorActive">
                <ColorPicker field="data.tocNumberBackgroundColorActive" />
              </FormGroup>

              <FormGroup label="tocNumberColorActive">
                <ColorPicker field="data.tocNumberColorActive" />
              </FormGroup>

              <FormGroup label="tocChapterBorderColor">
                <ColorPicker field="data.tocChapterBorderColor" />
              </FormGroup>

              <FormGroup label="tocSubheadingsBorderColor">
                <ColorPicker field="data.tocSubheadingsBorderColor" />
              </FormGroup>

              <FormGroup label="tocBackgroundImage">
                <Text field="data.tocBackgroundImage" />
              </FormGroup>
            </FormSection>

            <FormSection maxWidth="100%" title="Headings">
              <FormGroup label="H1 color">
                <ColorPicker field="data.headingOneColor" />
              </FormGroup>

              <FormGroup label="H2 color">
                <ColorPicker field="data.headingTwoColor" />
              </FormGroup>

              <FormGroup label="H3 color">
                <ColorPicker field="data.headingThreeColor" />
              </FormGroup>
            </FormSection>
          </div>
          <div
            style={{
              borderTop: '1px solid #dcdddf',
              padding: 15,
              width: '100%'
            }}
          >
            <FlexContainer style={{ justifyContent: 'space-between' }}>
              <ConfirmButton
                actionText={messages.commonSaveChanges}
                buttonText={messages.commonSaveChanges}
                category="success"
                confirmText={
                  'Are you sure you want to save changes to the theme?'
                }
                confirmTitle={messages.commonSaveChanges}
                onConfirm={handleSubmit}
              />

              <MoreOptions alignVertical="bottom" category="lighter">
                <SiteSettingsThemesImport
                  onImport={onSave}
                  themeId={readerThemeId}
                />
                <SiteSettingsThemesExport
                  value={JSON.stringify(formState.values.data)}
                />
              </MoreOptions>
            </FlexContainer>
          </div>
        </FlexContainer>
      )}
    </Form>
  );
};

export default SiteSettingsThemesEditorReader;
